import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import {
  locationIdParam, locationIdToPrefs, prefsToLocationId,
} from '../ChargeControl/chargeUi';
import type {
  ChargerView, LocationItem, ChargePrefs, GoalResult, LocationId,
  MemberItem, NearbyCluster, FleetResponse,
} from '../ChargeControl/types';

interface ChargeStore {
  fleet: ChargerView[];
  generatedAt: string | null;
  collectorOk: boolean;
  // Declared sites, from the collector's config. Arrives with every fleet
  // payload rather than from its own endpoint, and lists what was DECLARED, so
  // a site whose chargers are all powered down keeps its chip.
  sites: string[];
  locations: LocationItem[];
  prefs: ChargePrefs;
  currentLocation: LocationId;

  initialized: boolean;
  loading: boolean;            // initial prefs+locations load
  fleetLoaded: boolean;        // first fleet payload arrived (for spinner-vs-poll)
  error: string | null;
  fleetReq: number;            // request generation — only the latest fetchFleet commits

  init: () => Promise<void>;
  refreshLocations: () => Promise<void>;
  fetchFleet: () => Promise<void>;
  setCurrentLocation: (id: LocationId) => Promise<void>;
  setGoal: (args: { locationId?: LocationId; chargerIds?: string[]; percent: number }) => Promise<GoalResult>;
  pinCharger: (assetId: string) => Promise<void>;
  unpinCharger: (assetId: string) => Promise<void>;
  searchChargers: (q: string) => Promise<ChargerView[]>;
  findSameNetwork: (networkId: string | null) => Promise<ChargerView[]>;
  createLocation: (name: string, color?: string | null) => Promise<LocationItem>;
  updateLocation: (id: number, patch: { name?: string; color?: string | null }) => Promise<void>;
  deleteLocation: (id: number) => Promise<void>;
  getMembers: (id: number) => Promise<MemberItem[]>;
  setMembers: (id: number, assetIds: string[]) => Promise<void>;
  setPinned: (pinned: number[]) => Promise<void>;
  setVpnAccess: (v: boolean) => Promise<void>;
  fetchNearby: (lat: number, lng: number) => Promise<NearbyCluster[]>;
}

const DEFAULT_PREFS: ChargePrefs = { current_location: 'mine', has_vpn_access: false, pinned: [] };

const useChargeStore = create<ChargeStore>((set, get) => ({
  fleet: [],
  generatedAt: null,
  collectorOk: true,
  sites: [],
  locations: [],
  prefs: DEFAULT_PREFS,
  currentLocation: 'mine',
  initialized: false,
  loading: true,
  fleetLoaded: false,
  error: null,
  fleetReq: 0,

  init: async () => {
    set({ loading: true, error: null });
    try {
      const [prefs, locsRes] = await Promise.all([
        apiFetchAuthed('/charge/prefs') as Promise<ChargePrefs>,
        apiFetchAuthed('/charge/locations') as Promise<{ locations: LocationItem[] }>,
      ]);
      set({
        prefs,
        locations: locsRes.locations || [],
        currentLocation: prefsToLocationId(prefs.current_location),
        initialized: true,
        loading: false,
      });
      await get().fetchFleet();
    } catch (err: any) {
      set({ loading: false, error: err?.message || 'Failed to load charge control' });
    }
  },

  refreshLocations: async () => {
    const res = (await apiFetchAuthed('/charge/locations')) as { locations: LocationItem[] };
    set({ locations: res.locations || [] });
  },

  fetchFleet: async () => {
    const { currentLocation } = get();
    // Tag this request; a location switch starts a newer one, and only the
    // latest may commit — so a stale in-flight poll for the old location can't
    // clobber the freshly-switched view.
    const reqId = get().fleetReq + 1;
    set({ fleetReq: reqId });
    const qs = `location_id=${locationIdParam(currentLocation)}`;
    try {
      const res = (await apiFetchAuthed(`/charge/fleet?${qs}`, { silent: true })) as FleetResponse;
      if (get().fleetReq !== reqId) return; // superseded
      set({
        fleet: res.chargers || [],
        // Keep the last known site list when the collector is unreachable: the
        // chips must not vanish mid-outage and strand whoever is standing at one.
        sites: Array.isArray(res.sites) ? res.sites : get().sites,
        generatedAt: res.generated_at,
        collectorOk: res.collector_ok !== false,
        fleetLoaded: true,
        error: null,
      });
    } catch (err: any) {
      if (get().fleetReq !== reqId) return; // superseded
      // Poll failure shouldn't blank the screen — keep last fleet, flag the error.
      set({ collectorOk: false, fleetLoaded: true, error: err?.message || 'Fleet unavailable' });
    }
  },

  setCurrentLocation: async (id) => {
    set({ currentLocation: id, fleet: [], fleetLoaded: false });
    await get().fetchFleet();
    // Persist resume-on-open (best effort — don't block the view on it).
    try {
      const prefs = (await apiFetchAuthed('/charge/prefs', {
        method: 'PUT', body: { current_location: locationIdToPrefs(id) },
      })) as ChargePrefs;
      set({ prefs });
    } catch { /* non-fatal */ }
  },

  setGoal: async ({ locationId, chargerIds, percent }) => {
    const body: any = { percent };
    if (chargerIds && chargerIds.length) body.charger_ids = chargerIds;
    else body.location_id = locationId;   // 'mine' | <group id>
    const result = (await apiFetchAuthed('/charge/goal', { method: 'POST', body })) as GoalResult;
    get().fetchFleet();  // reflect the new goal
    return result;
  },

  pinCharger: async (assetId) => {
    await apiFetchAuthed('/charge/charger-pins', { method: 'POST', body: { asset_id: assetId } });
    if (get().currentLocation === 'mine') get().fetchFleet();  // reflect in My Chargers
  },

  unpinCharger: async (assetId) => {
    await apiFetchAuthed(`/charge/charger-pins/${assetId}`, { method: 'DELETE', skipJsonParse: true });
    if (get().currentLocation === 'mine') get().fetchFleet();
  },

  searchChargers: async (q) => {
    const res = (await apiFetchAuthed(
      `/charge/fleet?search=${encodeURIComponent(q)}`, { silent: true }
    )) as FleetResponse;
    return res.chargers || [];
  },

  // Online chargers sharing a network_id (same public-IP egress) — powers the
  // "pin more on the same network?" suggestion. OFFLINE chargers are excluded:
  // their live network_id is null/stale, and suggesting one could mislead.
  findSameNetwork: async (networkId) => {
    if (!networkId) return [];
    const all = await get().searchChargers('');
    return all.filter((c) => c.online && c.network_id === networkId);
  },

  createLocation: async (name, color = null) => {
    const row = (await apiFetchAuthed('/charge/locations', {
      method: 'POST', body: { name, color },
    })) as LocationItem;
    await get().refreshLocations();
    return row;
  },

  updateLocation: async (id, patch) => {
    await apiFetchAuthed(`/charge/locations/${id}`, { method: 'PUT', body: patch });
    await get().refreshLocations();
  },

  deleteLocation: async (id) => {
    await apiFetchAuthed(`/charge/locations/${id}`, { method: 'DELETE', skipJsonParse: true });
    // If we were viewing the deleted location, fall back to HQ.
    if (get().currentLocation === id) await get().setCurrentLocation('mine');
    await get().refreshLocations();
  },

  getMembers: async (id) => {
    const res = (await apiFetchAuthed(`/charge/locations/${id}/members`)) as { members: MemberItem[] };
    return res.members || [];
  },

  setMembers: async (id, assetIds) => {
    await apiFetchAuthed(`/charge/locations/${id}/members`, {
      method: 'PUT', body: { asset_ids: assetIds },
    });
    await get().refreshLocations();
    if (get().currentLocation === id) get().fetchFleet();
  },

  setPinned: async (pinned) => {
    const prefs = (await apiFetchAuthed('/charge/prefs', {
      method: 'PUT', body: { pinned },
    })) as ChargePrefs;
    set({ prefs });
    await get().refreshLocations();
  },

  setVpnAccess: async (v) => {
    const prefs = (await apiFetchAuthed('/charge/prefs', {
      method: 'PUT', body: { has_vpn_access: v },
    })) as ChargePrefs;
    set({ prefs });
  },

  fetchNearby: async (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      throw new Error('Invalid coordinates');
    }
    const res = (await apiFetchAuthed(
      `/charge/nearby?lat=${lat.toFixed(6)}&lng=${lng.toFixed(6)}`
    )) as { clusters: NearbyCluster[] };
    return res.clusters || [];
  },
}));

export default useChargeStore;
